import type * as appointments from '@seen/core/models/appointments/index'
import type { Order } from '@seen/core/models/orders/Order'

const REPORT_REQUEST =
  /\b(?:attach|bring|include|print|send|provide|fax|forward|upload)\b[^.;]{0,120}?\b(?:audiograms?|ct|images?|imaging|labs?|mri|pet|reports?|results?|scans?|ultrasounds?|x-?rays?)\b/i
// The specialist's own visit note or progress report, sent back to Seen.
const RETURN_REPORT_REQUEST =
  /\bsend (?:the |a |any )?(?:progress )?reports?\b/i
// Referral order template footer: what Seen asks the specialist to return.
const REFERRAL_FOOTER =
  /Please provide visit note upon completion[\s\S]*?(?:any questions\.?|$)/i
const RECORDS_NEEDED = /Records\/labs\/imaging needed for appt:\s*([^\n]*)/i
const NO_RECORDS_NEEDED = /^(?:n\/?a|none|per specialist['’]?s office)?\.?$/i
const RECORDS_CHECKLIST = /Records to bring:[^\n]*/i
const CHECKED_ITEM =
  /\[\s*x\s*\]\s*([^[]+?)(?=\s*\[|\s+(?:Labs|Imaging|Other):|$)/gi

export interface PacketReportReference {
  source: string
  text: string
}

export function packetReportReferences(
  appointment: appointments.ExtendedAppointment,
  orders: readonly Order[]
): PacketReportReference[] {
  return [
    reference('Appointment description', appointment.notes),
    reference('Appointment comment', appointment.comment),
    ...orders.flatMap((order) => {
      const source = order.seenIdentifier ?? 'Linked order'
      return [
        reference(source, order.description),
        reference(source, order.title),
        ...order.notes.map((note) => reference(source, note.text)),
      ]
    }),
  ].filter((match): match is PacketReportReference => match !== undefined)
}

function reference(
  source: string,
  value: string | undefined
): PacketReportReference | undefined {
  if (!value) {
    return undefined
  }
  let text = plainText(value).replace(REFERRAL_FOOTER, '\n')
  const requests: string[] = []

  const checklist = text.match(RECORDS_CHECKLIST)?.[0]
  if (checklist) {
    text = text.replace(checklist, '\n')
    const checked = [...checklist.matchAll(CHECKED_ITEM)].flatMap((match) => {
      const item = match[1]?.trim() ?? ''
      return item && !/^none needed$/i.test(item) ? [item] : []
    })
    if (checked.length > 0) {
      requests.push(`Records to bring: ${checked.join(', ')}`)
    }
  }

  const needed = text.match(RECORDS_NEEDED)
  if (needed) {
    text = text.replace(needed[0], '\n')
    const records = needed[1]?.trim() ?? ''
    if (!NO_RECORDS_NEEDED.test(records)) {
      requests.push(`Records needed: ${records}`)
    }
  }

  for (const sentence of text.split(/(?<=[.;!?])\s+|\s+-\s+|\n/)) {
    const trimmed = sentence.trim()
    if (REPORT_REQUEST.test(trimmed) && !RETURN_REPORT_REQUEST.test(trimmed)) {
      requests.push(trimmed)
    }
  }

  return requests.length > 0 ? { source, text: requests.join(' ') } : undefined
}

function plainText(value: string): string {
  return value
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\s+/g, ' ')
    .trim()
}
